import React, { Fragment, useState } from "react";
import styled from "styled-components";
import { Flex } from "../../../components/Flex";
import { Rounded } from "../../../theme/Rounded";
import { Button } from "../../../components/Button";
import { Spacing } from "../../../theme/Spacing";
import { Input } from "../../../components/Input";
import { Breakpoints } from "../../../theme/Breakpoints";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { appActions } from "../../../redux/app/app.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { appSliceSelectors } from "../../../redux/app/app.selector";
import { AtiraSelect } from "../../../components/AtiraSelect";
import camelCase from "lodash/camelCase";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";
import { SearchKind } from "../../../model/search/types/SearchKind.enum";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import omit from "lodash/omit";

const SearchWrapper = styled(Flex)`
  position: relative;
  display: none;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    display: flex;
    margin-inline-end: auto;
    border-radius: ${Rounded.pill};
    overflow: hidden;
  }
`;

const SearchInput = styled(Input)`
  width: 20rem !important;
  height: 1.8rem;
  padding: 0 ${Spacing.s} 0 ${Spacing.xl} !important;
  direction: ltr !important;
  border-radius: 0;
  border: 0 !important;
  border-inline-end: ${({ theme }) => `2px solid ${theme.sub}`} !important;

  html[dir="rtl"] & {
    padding: 0 ${Spacing.xl} 0 ${Spacing.s} !important;
    direction: rtl !important;
  }
`;

const SearchIconButton = styled(Button)`
  position: absolute;
  z-index: 1;
  inset-block: 0;
  inset-inline-start: 3px;
  width: ${Spacing.l};
  background-color: transparent;
  padding: 0;
  height: 1.5rem;
  margin: auto;
  aspect-ratio: 1 / 1;
  border-radius: ${Rounded.circle};
  color: ${({ theme }) => theme.sub};
  background-color: ${({ theme }) => theme.main};

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const SearchSelectWrapper = styled(Flex)`
  .ant-select {
    width: 6rem !important;
    border-radius: 0;
    height: 1.8rem !important;
    border: 0 !important;
    box-shadow: none !important;
  }
`;

export const SearchBar: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string | undefined>("");

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const searchPageSearchOptions = useAppSelector(
    appSliceSelectors.selectSearchOptions,
  );
  const isOrgMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);

  const handleSearch = () => {
    if (searchKeyword?.trim()) {
      dispatch(
        appActions.setSearchOptions({
          keyword: searchKeyword,
          kind: searchPageSearchOptions?.kind || SearchKind.ALL,
        }),
      );
      navigate("/search");
      setSearchKeyword("");
    }
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const onChangeSearchEntityKind = (kind: SearchKind) => {
    dispatch(appActions.setSearchOptions({ keyword: searchKeyword, kind }));
  };

  return (
    <Fragment>
      <SearchWrapper>
        <SearchInput
          placeholder={t("search.title")}
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onKeyDown={onSearchKeyDown}
        />
        <SearchSelectWrapper>
          <AtiraSelect
            options={Object.values(
              !isOrgMember ? SearchKind : omit(SearchKind, "MODULE_INSTANCE"),
            ).map((v) => ({
              label: t(`common.${camelCase(v)}`),
              value: v,
            }))}
            value={searchPageSearchOptions?.kind}
            onChange={(v) => onChangeSearchEntityKind(v as SearchKind)}
            rootClassName="header-search-dropdown"
          />
        </SearchSelectWrapper>

        <SearchIconButton icon={faSearch} onClick={handleSearch} />
      </SearchWrapper>
    </Fragment>
  );
};
